import { API_BASE_URL, type TranslationAppId } from '@types';

// ============================================================================
// TYPES
// ============================================================================

export interface TranslationData {
  [key: string]: string;
}

export interface LangResponse {
  data: {
    it: TranslationData;
    en: TranslationData;
    pt: TranslationData;
    fr: TranslationData;
    es: TranslationData;
    de: TranslationData;
  };
}

export interface UpdateLangRequest {
  it: TranslationData;
  en: TranslationData;
  pt: TranslationData;
  fr: TranslationData;
  es: TranslationData;
  de: TranslationData;
}

/** Locales accepted by POST /services/v1/lang/{id} (body.lang + X-Localization). */
export const TRANSLATION_API_LANG_CODES = [
  'it',
  'en',
  'pt',
  'fr',
  'es',
  'de',
] as const;

export type TranslationApiLangCode =
  (typeof TRANSLATION_API_LANG_CODES)[number];

/** All columns shown in admin (same set as API locales). */
export const TRANSLATION_LANG_CODES = TRANSLATION_API_LANG_CODES;

export type TranslationLangCode = (typeof TRANSLATION_LANG_CODES)[number];

/** Number of translation keys per locale (admin list). */
export type LangKeyCounts = Record<TranslationLangCode, number>;

export function getLangKeyCounts(data: LangResponse): LangKeyCounts {
  const out: LangKeyCounts = {
    it: 0,
    en: 0,
    pt: 0,
    fr: 0,
    es: 0,
    de: 0,
  };
  for (const code of TRANSLATION_LANG_CODES) {
    out[code] = Object.keys(data.data[code]).length;
  }
  return out;
}

/** App id in URL path (1 = Life Experience, 2 = Life Suite). */
function langPathSegment(appId: TranslationAppId): string {
  return String(appId);
}

function buildLangReadUrl(appId: TranslationAppId): string {
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}/services/v1/lang/${langPathSegment(appId)}`;
}

function buildLangUpdateUrl(appId: TranslationAppId): string {
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}/services/v1/lang/${langPathSegment(appId)}/update`;
}

/** Avoid custom headers beyond X-Localization: extra headers often fail browser CORS preflight. */
const langRequestHeaders = (lang: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  'X-Localization': lang,
});

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetches translations for one locale; app scope is the `{id}` path segment.
 */
const fetchLangByCode = async (
  lang: string,
  appId: TranslationAppId
): Promise<TranslationData> => {
  const response = await fetch(buildLangReadUrl(appId), {
    method: 'POST',
    credentials: 'include',
    headers: langRequestHeaders(lang),
    body: JSON.stringify({
      lang,
      tags: {},
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
};

/**
 * Fetches all supported languages for admin (read-only display for non-IT;
 * editing flow still targets Italian + save / save-and-translate).
 */
export const getLang = async (
  appId: TranslationAppId
): Promise<LangResponse> => {
  const apiResults = await Promise.all(
    TRANSLATION_API_LANG_CODES.map(code => fetchLangByCode(code, appId))
  );

  return {
    data: Object.fromEntries(
      TRANSLATION_API_LANG_CODES.map((code, index) => [code, apiResults[index]])
    ) as LangResponse['data'],
  };
};

/**
 * Update API merges entries; omitting a key leaves the old value on the server.
 * Keys that exist on the server but were removed from the Italian draft are sent
 * with an empty value so the backend can remove them; other locales get the same
 * for keys that existed there.
 */
export function applyTranslationKeyRemovalsForUpdate(
  draft: UpdateLangRequest,
  server: LangResponse['data']
): UpdateLangRequest {
  const deletedFromItalian = Object.keys(server.it).filter(
    k => !Object.hasOwn(draft.it, k)
  );

  if (deletedFromItalian.length === 0) {
    return draft;
  }

  const next: UpdateLangRequest = {
    it: { ...draft.it },
    en: { ...draft.en },
    pt: { ...draft.pt },
    fr: { ...draft.fr },
    es: { ...draft.es },
    de: { ...draft.de },
  };

  for (const k of deletedFromItalian) {
    next.it[k] = '';
    for (const code of TRANSLATION_LANG_CODES) {
      if (code === 'it') continue;
      if (Object.hasOwn(server[code], k)) {
        next[code][k] = '';
      }
    }
  }

  return next;
}

/**
 * Updates language translations for the app identified by `{id}` in the path.
 */
export const updateLang = async (
  data: UpdateLangRequest,
  appId: TranslationAppId
): Promise<{ success: boolean; message: string }> => {
  const languages = Object.keys(data) as Array<keyof UpdateLangRequest>;

  for (const lang of languages) {
    if (!TRANSLATION_API_LANG_CODES.includes(lang as TranslationApiLangCode)) {
      continue;
    }

    const translations = data[lang as TranslationApiLangCode];

    if (!translations || Object.keys(translations).length === 0) {
      continue;
    }

    const dataArray = Object.entries(translations).map(([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : String(value),
    }));

    const response = await fetch(buildLangUpdateUrl(appId), {
      method: 'POST',
      credentials: 'include',
      headers: langRequestHeaders(lang),
      body: JSON.stringify({
        lang,
        data: dataArray,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
  }

  return { success: true, message: 'Translations updated successfully' };
};
